import type { CardListItem, CardListQuery, CardSummary } from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import type { CardListFilter, CatalogueKind } from "../../core/ops/cards-ops";
import { formatCents } from "../../core/primitives/card-display";
import type { CardSection } from "../../core/primitives/card-edit";
import { idleLoad, type LoadStatus } from "../../core/primitives/load-status";

export const CARD_FILTERS: { key: CardListFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "in-stock", label: "In stock" },
  { key: "out-of-stock", label: "Out of stock" },
  { key: "untracked", label: "Untracked" },
];

export const CATALOGUE_KINDS: { key: CatalogueKind; label: string }[] = [
  { key: "all", label: "Everything" },
  { key: "card", label: "Cards" },
  { key: "sealed", label: "Sealed" },
];

/** The Undo bar: a count not yet sent, or a price a lookup already wrote. Its line is the title, then the change. */
export type StockUndo =
  | { readonly kind: "stock"; readonly cardId: string; readonly title: string; readonly change: string }
  | {
      readonly kind: "price";
      readonly cardId: string;
      readonly title: string;
      readonly change: string;
      readonly fromCents: number | null;
    };

/** What the Stock list shows under its head. */
export type StockListBody = "loading" | "no-match" | "nothing-yet" | "list";

/**
 * The list's chips and body. The chips stay whenever there is a catalogue to narrow, or a narrowing to undo. A page
 * that came back empty with more pages after it is no empty state: the list reads on. Nor is a read that failed: Retry
 * says what went wrong.
 */
export function stockListView(v: {
  readonly count: number;
  readonly more: boolean;
  readonly status: LoadStatus;
  readonly search: string;
  readonly filter: CardListFilter;
  readonly kind?: CatalogueKind;
}): { readonly chips: boolean; readonly body: StockListBody } {
  const narrowed = v.search.trim() !== "" || v.filter !== "all" || (v.kind ?? "all") !== "all";
  const chips = v.count > 0 || narrowed;
  if (v.count > 0 || v.more) return { chips, body: "list" };
  if (v.status.kind === "idle" || v.status.kind === "loading") return { chips, body: "loading" };
  if (v.status.kind === "loaded") return { chips, body: narrowed ? "no-match" : "nothing-yet" };
  return { chips, body: "list" };
}

/** A row starts nothing while its own write goes, while a card opens, or while the list Back showed is read again. */
export function stockRowHeld(
  cardId: string,
  v: { readonly busy: ReadonlySet<string>; readonly opening: string | null; readonly refreshing: boolean },
): boolean {
  return v.busy.has(cardId) || v.opening !== null || v.refreshing;
}

export class CardsState {
  readonly filter = new Rx<CardListFilter>("all");
  /** Cards, sealed product, or both — both unless the merchant narrows it. */
  readonly kind = new Rx<CatalogueKind>("all");
  readonly search = new Rx("");
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly items = new RxList<CardListItem>();
  /** The page after the last one shown, or null when the list is complete. */
  readonly nextCursor = new Rx<string | null>(null);
  /** Whether the caller may reprice, count and archive. `true` until the role is known. */
  readonly canEditInventory = new Rx(true);
  /** Counts tapped on a row and not yet read back, by card id. */
  readonly stockEdits = new Rx<Readonly<Record<string, number>>>({});
  /** Tracked stock at asking price; null while more pages exist. */
  readonly stockValueCents = new Rx<number | null>(null);
  /** Scans waiting in the review queue, for the Review tab. */
  readonly reviewCount = new Rx(0);
  /** Cards whose price is being looked up. */
  readonly repricing = new Rx<ReadonlySet<string>>(new Set());
  /** What the last lookup did to each card's price, until its next lookup or Undo. */
  readonly outcomes = new Rx<Readonly<Record<string, RepriceOutcome>>>({});
  /** Cards being archived, having a price put back, or waiting for a write another page left: their rows wait. */
  readonly busy = new Rx<ReadonlySet<string>>(new Set());
  /** Back showed the list as it was left: every row waits until the list is read again. */
  readonly refreshing = new Rx(false);
  readonly undo = new Rx<StockUndo | null>(null);
  /** The card being opened once its count lands. */
  readonly opening = new Rx<string | null>(null);
  /** Where Back from a card puts the page and the focus: the place it was left at and the card opened, until the view is there. */
  readonly scrollTo = new Rx<{ readonly y: number; readonly cardId: string } | null>(null);
  /** What went wrong on the list: an archive, a count, an Undo. */
  readonly error = new Rx<string | null>(null);
}

export abstract class CardsActions {
  abstract setFilter(filter: CardListFilter): void;
  abstract setKind(kind: CatalogueKind): void;
  abstract setSearch(search: string): void;
  abstract loadMore(): Promise<void>;
  abstract retry(): void;
  /** Asks the market again; the row says what it did, and Undo puts a changed price back. */
  abstract reprice(card: CardSummary): Promise<void>;
  /** One copy more or fewer; taps on one card add up into one change, sent once its Undo runs out. */
  abstract stepStock(card: CardSummary, step: number): void;
  /** Archives without asking, as the row's menu and swipe do on iOS. */
  abstract archive(card: CardSummary): Promise<void>;
  abstract undo(): Promise<void>;
  /** Undo's time stops while the bar has the focus or the pointer. */
  abstract holdUndo(): void;
  abstract releaseUndo(): void;
  /** `scrollY` is where the list is left, for Back. */
  abstract openCard(card: CardSummary, section?: CardSection, scrollY?: number): void;
  /** The view scrolled to where Back put it. */
  abstract placeRestored(): void;
  abstract dismissError(): void;
}

/** The Stock list's filter, kind and search as the URL keeps them, so Back from a card lands on the same list. */
export function stockQuery(
  filter: NonNullable<CardListQuery["filter"]>,
  search: string,
  kind: CatalogueKind = "all",
): string {
  const query = new URLSearchParams();
  if (filter !== "all") query.set("filter", filter);
  if (kind !== "all") query.set("kind", kind);
  if (search) query.set("q", search);
  return query.toString();
}

/** The Stock row's title, as iOS's: the card's name, else the player, else the team. */
export function stockTitle(card: Pick<CardSummary, "cardFullName" | "player" | "team">): string {
  return card.cardFullName?.trim() || card.player.trim() || card.team.trim() || "Untitled card";
}

/** The row's "…" menu, as iOS's: History for every role; setting a price, counting and archiving for editors. */
export function rowMenu(canEdit: boolean, tracked: boolean): ("price" | "count" | "history" | "archive")[] {
  if (!canEdit) return ["history"];
  return tracked ? ["price", "count", "history", "archive"] : ["price", "history", "archive"];
}

/** What the listing online asks, when it disagrees with the shop: the listing has not caught up. */
export function listedOnline(item: Pick<CardListItem, "card" | "providerPriceCents">): number | null {
  const mine = item.card.currentPriceCents;
  const theirs = item.providerPriceCents;
  return mine != null && theirs != null && mine !== theirs ? theirs : null;
}

/** The no-match line: the search, else the chip, that nothing matches. */
export function noMatchText(search: string, filterLabel: string): string {
  return `Nothing in the catalogue matches “${search.trim() || filterLabel}”.`;
}

/** "Oct 27, 2026", whatever the browser's language, as iOS writes it. */
export function pricedDate(at: Date): string {
  return at.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** What a re-price did to a card's asking price. */
export type RepriceOutcome =
  | { readonly kind: "found"; readonly fromCents: number | null; readonly toCents: number }
  | { readonly kind: "none" }
  | { readonly kind: "failed"; readonly message: string };

/** The line under a row's price: the move, "No change", "first price", that eBay had nothing, or why it failed. */
export function outcomeLine(outcome: RepriceOutcome): { text: string; tone: "up" | "down" | "muted" | "danger" } {
  if (outcome.kind === "failed") return { text: outcome.message, tone: "danger" };
  if (outcome.kind === "none") return { text: "No eBay comps found", tone: "muted" };
  const { fromCents, toCents } = outcome;
  if (fromCents == null) return { text: "first price", tone: "muted" };
  const delta = toCents - fromCents;
  if (delta === 0) return { text: "No change", tone: "muted" };
  const pct = fromCents > 0 ? Math.round((Math.abs(delta) / fromCents) * 100) : 0;
  return {
    text: `${delta > 0 ? "▲" : "▼"} ${formatCents(Math.abs(delta))} (${pct}%)`,
    tone: delta > 0 ? "up" : "down",
  };
}

/** What the Undo bar says a re-price did, after the card's title. */
export function priceChangeText(fromCents: number | null, toCents: number): string {
  return `price ${fromCents == null ? "set to " : `${formatCents(fromCents)} → `}${formatCents(toCents)}`;
}

/** What the Undo bar says taps waiting to be sent will do, after the card's title. */
export function stockChangeText(from: number, to: number): string {
  return `stock ${from} → ${to}`;
}

/** Ctrl/⌘+Z, which takes back what the Undo bar offers; a field keeps the key for its own undo. */
export function isUndoKey(
  key: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "shiftKey" | "altKey">,
  inField: boolean,
): boolean {
  return !inField && (key.ctrlKey || key.metaKey) && !key.shiftKey && !key.altKey && key.key.toLowerCase() === "z";
}

/**
 * A count that threw on its way may have gone through, so its card is read again, or its row waits for the list when
 * that read fails. Said far from the row, so it names the card, and true both while the row waits and once it is free.
 */
export function stockFailedText(title: string, read: boolean): string {
  return read
    ? `Something went wrong while saving the count of ${title}. Check its count before counting again.`
    : `Something went wrong while saving the count of ${title}, and it could not be read again. Check its count once its row is free, before counting again.`;
}

export const UNDO_FAILED_ON_THE_WAY = "Something went wrong while undoing. The card shows its price now.";
